import axios from 'axios';
import { auth } from './firebase';
import { signOut } from 'firebase/auth';

// Use environment variable for API URL, fallback to local development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const login = async (username, password) => {
  const params = new URLSearchParams();
  params.append('username', username);
  params.append('password', password);

  try {
    const response = await api.post('/token', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    localStorage.setItem('access_token', response.data.access_token);
    return response.data;
  } catch (error) {
    console.error('Login error:', error.response?.data || error.message);
    throw error;
  }
};

export const loginWithFirebaseToken = async (firebaseIdToken) => {
  try {
    const response = await api.post('/firebase-token', {
      id_token: firebaseIdToken,
    });
    localStorage.setItem('access_token', response.data.access_token);
    return response.data;
  } catch (error) {
    console.error('Firebase login error:', error.response?.data || error.message);
    throw error;
  }
};



export const getImages = async (page = 1, per_page = 18, search_query = null, { tags = [], editorialOnly = false } = {}) => {
  try {
    const params = { page, per_page };
    if (search_query) {
      params.q = search_query;
    }
    if (tags.length) {
      params.tags = tags;
    }
    if (editorialOnly) {
      params.editorial_only = true;
    }
    // tags=a&tags=b (FastAPI's list form), not axios's default tags[]=a.
    const response = await api.get('/images', { params, paramsSerializer: { indexes: null } });
    return response.data;
  } catch (error) {
    console.error('Get images error:', error.response?.data || error.message);
    throw error;
  }
};

export const getDomains = async () => {
  try {
    const response = await api.get('/domains');
    return response.data;
  } catch (error) {
    console.error('Get domains error:', error.response?.data || error.message);
    throw error;
  }
};

export const getPageHits = async () => {
  try {
    const response = await api.get('/analytics/page-hits');
    return response.data;
  } catch (error) {
    console.error('Get page hits error:', error.response?.data || error.message);
    return { hits: {}, updated_at: null, period_days: 30 };
  }
};

export const getAdSenseEarnings = async () => {
  try {
    const response = await api.get('/analytics/adsense-earnings');
    return response.data;
  } catch (error) {
    console.error('Get AdSense earnings error:', error.response?.data || error.message);
    return { earnings: {}, updated_at: null, period_days: 30 };
  }
};

export const getAdImpressions = async () => {
  try {
    const response = await api.get('/analytics/ad-impressions');
    return response.data;
  } catch (error) {
    console.error('Get ad impressions error:', error.response?.data || error.message);
    return { domains: {}, updated_at: null, period_days: 30 };
  }
};

export const refreshPageStats = async () => {
  try {
    const response = await api.post('/domains/refresh-page-stats');
    return response.data;
  } catch (error) {
    console.error('Refresh page stats error:', error.response?.data || error.message);
    throw error;
  }
};

export const getDomain = async (domainId) => {
  try {
    const response = await api.get(`/domain/${domainId}`);
    return response.data;
  } catch (error) {
    console.error('Get domain error:', error.response?.data || error.message);
    throw error;
  }
};

export const getPages = async (domainId) => {
  const response = await api.get(`/domain/${domainId}/pages`);
  return response.data;
};

/* The list columns only: title, state and dates, projected server-side. The
   full getPages ships every page's introduction_md -- 11.4 MB for a domain with
   2,494 pages, all of it discarded by a table showing four fields. Use this
   anywhere a view lists or picks pages rather than editing one. */
/* templates: also the site's template routes (source 'template') and stats-only stubs
   marked source 'stub'; the page list only, not the pickers. */
export const getPageSummaries = async (domainId, { templates = false } = {}) => {
  const response = await api.get(`/domain/${domainId}/pages/summary`, templates ? { params: { templates: true } } : undefined);
  return response.data;
};

/* Thin-page verdicts for every page: {pages: {slug: {thin, reasons, exempt}},
   thin, total, rules, sections_included}. Fetched beside the summaries so the
   table renders first and the badges fill in. */
export const getPagesThin = async (domainId) => {
  const response = await api.get(`/domain/${domainId}/pages/thin`);
  return response.data;
};

/* The domain's thin-page rules: {thin_rules, overrides, defaults, is_default}. */
export const getDomainSeo = async (domainId) => {
  const response = await api.get(`/domain/${domainId}/seo`);
  return response.data;
};

/* Template routes: content pages the site's templates render, registered for topic
   analysis and site search: {routes: [{id, slug, title, description, searchable, words, first_seen_at, fetched_at}]}. */
export const getSiteRoutes = async (domainId) => {
  const response = await api.get(`/domain/${domainId}/site-routes`);
  return response.data;
};

/* Sync routes from the live sitemap: {add, refresh, drop, taken_over, failed, indexed?}. Dry run unless apply. */
export const syncSiteRoutes = async (domainId, apply = false) => {
  const response = await api.post(`/domain/${domainId}/site-routes/sync`, null, { params: { apply } });
  return response.data;
};

export const setSiteRouteSearchable = async (domainId, routeId, searchable) => {
  const response = await api.patch(`/domain/${domainId}/site-routes/${encodeURIComponent(routeId)}`, { searchable });
  return response.data;
};

/* Article thumbs: {enabled, pages: [{page_id, slug, title, up, down, messages, updated_at}], totals}. */
export const getDomainPageFeedback = async (domainId) => {
  const response = await api.get(`/domain/${domainId}/page-feedback`);
  return response.data;
};

export const updateDomainPageFeedback = async (domainId, enabled) => {
  const response = await api.put(`/domain/${domainId}/page-feedback`, { enabled });
  return response.data;
};

/* The "Featured" strip at the top of every page (domain field skyscraper):
   {skyscraper: {text, link} | null, page: {id, title, status} | null, live_within_minutes: [5, 15]}. */
export const getFeaturedStrip = async (domainId) => {
  const response = await api.get(`/domain/${domainId}/featured-strip`);
  return response.data;
};

export const setFeaturedStrip = async (domainId, text, link) => {
  const response = await api.put(`/domain/${domainId}/featured-strip`, { text, link });
  return response.data;
};

export const removeFeaturedStrip = async (domainId) => {
  const response = await api.delete(`/domain/${domainId}/featured-strip`);
  return response.data;
};

/* Replace the stored overrides; keys left out follow the defaults. */
export const updateDomainSeo = async (domainId, thinRules) => {
  const response = await api.put(`/domain/${domainId}/seo`, { thin_rules: thinRules });
  return response.data;
};

/* Per-page hits/impressions/clicks, read live from BigQuery rather than from
   the Firestore mirror the cron used to populate. Returns empty metrics rather
   than throwing, so the pages table still renders if the query fails. */
export const getPageMetrics = async (domainId, days = 30) => {
  try {
    const response = await api.get(
      `/analytics/domain/${domainId}/page-metrics`, { params: { days } });
    return response.data;
  } catch (error) {
    console.error('Get page metrics error:', error.response?.data || error.message);
    return { pages: [], totals: {}, earnings_30d: 0, updated_at: null };
  }
};

/* Read rate and median read time per domain, from Snowplow page pings, live
   from BigQuery. Null on failure: the columns then read "—", not 0%. */
export const getReadTime = async (days = 30) => {
  try {
    const response = await api.get('/analytics/read-time', { params: { days } });
    return response.data;
  } catch (error) {
    console.error('Get read time error:', error.response?.data || error.message);
    return null;
  }
};

/* Amazon offer impressions/clicks (our own Snowplow counts, not Amazon's),
   live from BigQuery. Empty rather than throwing, like getPageMetrics, so the
   tables that merge them in still render if the query fails. */
export const getAmazonOffers = async (days = 30) => {
  try {
    const response = await api.get('/analytics/amazon-offers', { params: { days } });
    return response.data;
  } catch (error) {
    console.error('Get Amazon offers error:', error.response?.data || error.message);
    return { domains: {}, updated_at: null, period_days: days };
  }
};

export const getDomainAmazonOffers = async (domainId, days = 30, refresh = false) => {
  try {
    const response = await api.get(
      `/analytics/domain/${domainId}/amazon-offers`, { params: refresh ? { days, refresh } : { days } });
    return response.data;
  } catch (error) {
    console.error('Get domain Amazon offers error:', error.response?.data || error.message);
    return { products: [], pages: [], variants: [], totals: {}, updated_at: null, error: true };
  }
};

export const getMediaDetail = async (mediaId) => {
  const response = await api.get(`/media/${mediaId}`);
  return response.data;
};

export const getMediaUrl = async (mediaId) => {
  if (!mediaId) {
    return null;
  }
  const response = await api.get(`/media/${mediaId}/url`);
  return response.data.url;
};

export const getPage = async (domainId, pageId) => {
  try {
    const response = await api.get(`/domain/${domainId}/pages/${pageId}`);
    return response.data;
  } catch (error) {
    console.error('Get page error:', error.response?.data || error.message);
    throw error;
  }
};

export const updateDomainFavorite = async (domainId, isFavorite) => {
  try {
    const response = await api.patch(`/domain/${domainId}/favorite`, {
      is_favorite: isFavorite
    });
    return response.data;
  } catch (error) {
    console.error('Update domain favorite error:', error.response?.data || error.message);
    throw error;
  }
};

export const getDomainFavorites = async (domainId) => {
  try {
    const response = await api.get(`/domain/${domainId}/favorites`);
    return response.data;
  } catch (error) {
    console.error('Get domain favorites error:', error.response?.data || error.message);
    throw error;
  }
};

export const setDomainFavorites = async (domainId, favoritedBy) => {
  try {
    const response = await api.put(`/domain/${domainId}/favorites`, {
      favorited_by: favoritedBy
    });
    return response.data;
  } catch (error) {
    console.error('Set domain favorites error:', error.response?.data || error.message);
    throw error;
  }
};

export const updateDomain = async (domainId, domainData) => {
  try {
    const response = await api.patch(`/domain/${domainId}`, domainData);
    return response.data;
  } catch (error) {
    console.error('Update domain error:', error.response?.data || error.message);
    throw error;
  }
};

export const updatePage = async (domainId, pageId, pageData) => {
  try {
    const response = await api.put(`/domain/${domainId}/pages/${pageId}`, pageData);
    return response.data;
  } catch (error) {
    console.error('Update page error:', error.response?.data || error.message);
    throw error;
  }
};

export const createPage = async (domainId, pageData) => {
  try {
    const response = await api.post(`/domain/${domainId}/pages`, pageData);
    return response.data;
  } catch (error) {
    console.error('Create page error:', error.response?.data || error.message);
    throw error;
  }
};

export const quickCreatePage = async (domainId, title) => {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
  const now = new Date().toISOString();
  const pageData = {
    slug,
    title,
    status: 'draft',
    created_at: now,
    updated_at: now,
  };
  return createPage(domainId, pageData);
};

export const addFeaturedPage = async (domainId, pageId, position) => {
  try {
    const response = await api.post(`/domain/${domainId}/featured-pages`, {
      page_id: pageId,
      position: position
    });
    return response.data;
  } catch (error) {
    console.error('Add featured page error:', error.response?.data || error.message);
    throw error;
  }
};

export const removeFeaturedPage = async (domainId, pageId) => {
  try {
    const response = await api.delete(`/domain/${domainId}/featured-pages/${pageId}`);
    return response.data;
  } catch (error) {
    console.error('Remove featured page error:', error.response?.data || error.message);
    throw error;
  }
};

export const reorderFeaturedPage = async (domainId, pageId, newPosition) => {
  try {
    const response = await api.patch(`/domain/${domainId}/featured-pages/reorder`, {
      page_id: pageId,
      new_position: newPosition
    });
    return response.data;
  } catch (error) {
    console.error('Reorder featured page error:', error.response?.data || error.message);
    throw error;
  }
};

export const copyImageToWebsiteBucket = async (mediaId) => {
  try {
    const response = await api.post(`/media/${mediaId}/copy_for_main_image`);
    return response.data;
  } catch (error) {
    console.error('Copy image error:', error.response?.data || error.message);
    throw error;
  }
};

export const removeMainImage = async (domainId, pageId) => {
  try {
    const response = await api.delete(`/domain/${domainId}/pages/${pageId}/remove-main-image`);
    return response.data;
  } catch (error) {
    console.error('Remove main image error:', error.response?.data || error.message);
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem('access_token');
  signOut(auth).catch((err) => console.error('Firebase sign-out error:', err));
};

export const searchPages = async (page = 1, per_page = 20, search_query = null, domain_id = null) => {
  try {
    const params = { page, per_page };
    if (search_query) {
      params.q = search_query;
    }
    if (domain_id) {
      params.domain_id = domain_id;
    }
    const response = await api.get('/pages/search', { params });
    return response.data;
  } catch (error) {
    console.error('Search pages error:', error.response?.data || error.message);
    throw error;
  }
};

// Redirects API functions
export const getRedirects = async (domainId) => {
  const response = await api.get(`/domain/${domainId}/redirects`);
  return response.data;
};

export const getRedirect = async (domainId, redirectId) => {
  try {
    // The redirectId from useParams() might be URL-decoded by React Router
    // We need to double-encode it for the API call since the backend expects double-encoded IDs
    const doubleEncodedRedirectId = encodeURIComponent(encodeURIComponent(redirectId));
    const response = await api.get(`/domain/${domainId}/redirects/${doubleEncodedRedirectId}`);
    return response.data;
  } catch (error) {
    console.error('Get redirect error:', error.response?.data || error.message);
    throw error;
  }
};

export const updateRedirect = async (domainId, redirectId, redirectData) => {
  try {
    // The redirectId from useParams() might be URL-decoded by React Router
    // We need to double-encode it for the API call since the backend expects double-encoded IDs
    const doubleEncodedRedirectId = encodeURIComponent(encodeURIComponent(redirectId));
    const response = await api.put(`/domain/${domainId}/redirects/${doubleEncodedRedirectId}`, redirectData);
    return response.data;
  } catch (error) {
    console.error('Update redirect error:', error.response?.data || error.message);
    throw error;
  }
};

export const createRedirect = async (domainId, redirectData) => {
  try {
    // Backend expects source_path rather than id; the value is passed through as-is.
    const { id, ...processedRedirectData } = redirectData;
    processedRedirectData.source_path = id;
    
    const response = await api.post(`/domain/${domainId}/redirects`, processedRedirectData);
    return response.data;
  } catch (error) {
    console.error('Create redirect error:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteRedirect = async (domainId, redirectId) => {
  try {
    // The redirectId from useParams() might be URL-decoded by React Router
    // We need to double-encode it for the API call since the backend expects double-encoded IDs
    const doubleEncodedRedirectId = encodeURIComponent(encodeURIComponent(redirectId));
    const response = await api.delete(`/domain/${domainId}/redirects/${doubleEncodedRedirectId}`);
    return response.data;
  } catch (error) {
    console.error('Delete redirect error:', error.response?.data || error.message);
    throw error;
  }
};

// Page Sections API
/* Every section call takes an optional { revision: true } to work on the
   page's pending revision (page_drafts/<slug>) instead of the live page. */
const revisionParams = (opts) => (opts && opts.revision ? { params: { revision: true } } : undefined);

export const getPageSections = async (domainId, pageId, opts = {}) => {
  try {
    const response = await api.get(`/domain/${domainId}/pages/${encodeURIComponent(pageId)}/sections`, revisionParams(opts));
    return response.data;
  } catch (error) {
    console.error('Get page sections error:', error.response?.data || error.message);
    throw error;
  }
};

export const createPageSection = async (domainId, pageId, sectionData, opts = {}) => {
  try {
    const response = await api.post(`/domain/${domainId}/pages/${encodeURIComponent(pageId)}/sections`, sectionData, revisionParams(opts));
    return response.data;
  } catch (error) {
    console.error('Create page section error:', error.response?.data || error.message);
    throw error;
  }
};

export const updatePageSection = async (domainId, pageId, sectionId, sectionData, opts = {}) => {
  try {
    const response = await api.put(`/domain/${domainId}/pages/${encodeURIComponent(pageId)}/sections/${sectionId}`, sectionData, revisionParams(opts));
    return response.data;
  } catch (error) {
    console.error('Update page section error:', error.response?.data || error.message);
    throw error;
  }
};

export const deletePageSection = async (domainId, pageId, sectionId, opts = {}) => {
  try {
    const response = await api.delete(`/domain/${domainId}/pages/${encodeURIComponent(pageId)}/sections/${sectionId}`, revisionParams(opts));
    return response.data;
  } catch (error) {
    console.error('Delete page section error:', error.response?.data || error.message);
    throw error;
  }
};

export const reorderPageSections = async (domainId, pageId, sectionOrder, opts = {}) => {
  try {
    const response = await api.patch(`/domain/${domainId}/pages/${encodeURIComponent(pageId)}/sections/reorder`, sectionOrder, revisionParams(opts));
    return response.data;
  } catch (error) {
    console.error('Reorder page sections error:', error.response?.data || error.message);
    throw error;
  }
};

// User Management API
export const getUsers = async () => {
  try {
    const response = await api.get('/users');
    return response.data;
  } catch (error) {
    console.error('Get users error:', error.response?.data || error.message);
    throw error;
  }
};

export const updateUserAccess = async (uid, allowed) => {
  try {
    const response = await api.put(`/users/${uid}/access?allowed=${allowed}`);
    return response.data;
  } catch (error) {
    console.error('Update user access error:', error.response?.data || error.message);
    throw error;
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await api.get('/me');
    return response.data;
  } catch (error) {
    console.error('Get current user error:', error.response?.data || error.message);
    throw error;
  }
};

// API Key Management
export const getApiKeys = async () => {
  try {
    const response = await api.get('/api-keys');
    return response.data;
  } catch (error) {
    console.error('Get API keys error:', error.response?.data || error.message);
    throw error;
  }
};

export const createApiKey = async (name = 'Default') => {
  try {
    const response = await api.post(`/api-keys?name=${encodeURIComponent(name)}`);
    return response.data;
  } catch (error) {
    console.error('Create API key error:', error.response?.data || error.message);
    throw error;
  }
};

export const revokeApiKey = async (keyId) => {
  try {
    const response = await api.delete(`/api-keys/${keyId}`);
    return response.data;
  } catch (error) {
    console.error('Revoke API key error:', error.response?.data || error.message);
    throw error;
  }
};

// ====================================
// Bookmarks
// ====================================

export const getBookmarks = async (params) => {
  try {
    const response = await api.get('/bookmarks', { params });
    return response.data;
  } catch (error) {
    console.error('Get bookmarks error:', error.response?.data || error.message);
    throw error;
  }
};

export const createBookmark = async (bookmarkData) => {
  try {
    const response = await api.post('/bookmarks', bookmarkData);
    return response.data;
  } catch (error) {
    console.error('Create bookmark error:', error.response?.data || error.message);
    throw error;
  }
};

export const updateBookmark = async (bookmarkId, bookmarkData) => {
  try {
    const response = await api.put(`/bookmarks/${bookmarkId}`, bookmarkData);
    return response.data;
  } catch (error) {
    console.error('Update bookmark error:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteBookmark = async (bookmarkId) => {
  try {
    const response = await api.delete(`/bookmarks/${bookmarkId}`);
    return response.data;
  } catch (error) {
    console.error('Delete bookmark error:', error.response?.data || error.message);
    throw error;
  }
};

// ====================================
// Emails (from cbot-engine Datastore)
// ====================================

export const getEmails = async (params) => {
  try {
    const response = await api.get('/emails', { params });
    return response.data;
  } catch (error) {
    console.error('Get emails error:', error.response?.data || error.message);
    throw error;
  }
};

export const getEmailDomains = async () => {
  try {
    const response = await api.get('/emails/domains');
    return response.data;
  } catch (error) {
    console.error('Get email domains error:', error.response?.data || error.message);
    throw error;
  }
};

export const deleteEmail = async (emailId) => {
  try {
    const response = await api.delete(`/emails/${emailId}`);
    return response.data;
  } catch (error) {
    console.error('Delete email error:', error.response?.data || error.message);
    throw error;
  }
};

/* ---- editorial status ---------------------------------------------------- */

/* PATCH only status / published_at; the page body is untouched.
   -> { message, page_id, status, published_at } */
export const setPageStatus = async (domainId, pageId, status, publishedAt) => {
  const body = { status };
  if (publishedAt !== undefined) body.published_at = publishedAt;
  try {
    const response = await api.patch(
      `/domain/${domainId}/pages/${encodeURIComponent(pageId)}/status`, body);
    return response.data;
  } catch (error) {
    console.error('Set page status error:', error.response?.data || error.message);
    throw error;
  }
};

/* -> { updated: n, failed: [page_id, ...] } */
export const bulkSetPageStatus = async (domainId, pageIds, status, publishedAt) => {
  const body = { page_ids: pageIds, status };
  if (publishedAt !== undefined) body.published_at = publishedAt;
  try {
    const response = await api.post(`/domain/${domainId}/pages/bulk-status`, body);
    return response.data;
  } catch (error) {
    console.error('Bulk set page status error:', error.response?.data || error.message);
    throw error;
  }
};

/* Pages across every domain due to go live in the next `days` days.
   -> { pages: [{domain_id, hostname, slug, title, published_at, status}], days, generated_at }
   Returns an empty list rather than throwing so a dashboard still renders. */
export const getScheduledPages = async (days = 14) => {
  try {
    const response = await api.get('/analytics/scheduled-pages', { params: { days } });
    return response.data;
  } catch (error) {
    console.error('Get scheduled pages error:', error.response?.data || error.message);
    return { pages: [], days, generated_at: null };
  }
};

/* -> { drafts: [{slug,title,updated_at}], missing_description: [...],
        image_error: [...], scheduled: [{slug,title,published_at}], generated_at } */
export const getDomainAttention = async (domainId) => {
  try {
    const response = await api.get(`/domain/${domainId}/attention`);
    return response.data;
  } catch (error) {
    console.error('Get domain attention error:', error.response?.data || error.message);
    return { drafts: [], missing_description: [], image_error: [], scheduled: [], generated_at: null };
  }
};

/* ---- preview tokens and pending revisions -------------------------------- */

/* A signed preview token for one site (default 12h, at most 7 days), plus a
   ready preview URL on the site's real host for `slug` (home when omitted).
   -> { token, domain_id, ttl, exp, expires_at, host, preview_url } */
export const mintPreviewToken = async (domainId, { slug, ttl } = {}) => {
  const params = {};
  if (slug !== undefined && slug !== null) params.slug = slug;
  if (ttl) params.ttl = ttl;
  try {
    const response = await api.post(`/domain/${domainId}/preview-token`, null, { params });
    return response.data;
  } catch (error) {
    console.error('Mint preview token error:', error.response?.data || error.message);
    throw error;
  }
};

/* -> { page_ids: [slug, ...] } for every page with a pending revision */
export const getPageRevisionIds = async (domainId) => {
  try {
    const response = await api.get(`/domain/${domainId}/pages/revisions`);
    return response.data?.page_ids || [];
  } catch (error) {
    console.error('Get page revisions error:', error.response?.data || error.message);
    return [];
  }
};

/* The pending revision, in the same shape as getPage. */
export const getPageRevision = async (domainId, pageId) => {
  try {
    const response = await api.get(`/domain/${domainId}/pages/${encodeURIComponent(pageId)}/revision`);
    return response.data;
  } catch (error) {
    console.error('Get page revision error:', error.response?.data || error.message);
    throw error;
  }
};

/* Save the page object as the pending revision; the live page is untouched.
   -> { message, page_id, created, has_revision } */
export const savePageRevision = async (domainId, pageId, pageData) => {
  try {
    const response = await api.post(`/domain/${domainId}/pages/${encodeURIComponent(pageId)}/revision`, pageData);
    return response.data;
  } catch (error) {
    console.error('Save page revision error:', error.response?.data || error.message);
    throw error;
  }
};

export const discardPageRevision = async (domainId, pageId) => {
  try {
    const response = await api.delete(`/domain/${domainId}/pages/${encodeURIComponent(pageId)}/revision`);
    return response.data;
  } catch (error) {
    console.error('Discard page revision error:', error.response?.data || error.message);
    throw error;
  }
};

/* Copy the revision over the live page, then delete it. */
export const publishPageRevision = async (domainId, pageId) => {
  try {
    const response = await api.post(`/domain/${domainId}/pages/${encodeURIComponent(pageId)}/revision/publish`);
    return response.data;
  } catch (error) {
    console.error('Publish page revision error:', error.response?.data || error.message);
    throw error;
  }
};

// ====================================
// Product library (Save to DAM extension)
// ====================================

const call = async (label, promise) => {
  try {
    const response = await promise;
    return response.data;
  } catch (error) {
    console.error(`${label} error:`, error.response?.data || error.message);
    throw error;
  }
};

export const getProducts = (domainId, page = null) =>
  call('Get products', api.get(`/domain/${domainId}/products`, { params: page ? { page } : {} }));

export const updateProduct = (domainId, asin, changes) =>
  call('Update product', api.patch(`/domain/${domainId}/products/${encodeURIComponent(asin)}`, changes));

export const deleteProduct = (domainId, asin) =>
  call('Delete product', api.delete(`/domain/${domainId}/products/${encodeURIComponent(asin)}`));

export const getExtensionTokens = () => call('Get extension tokens', api.get('/extension-tokens'));

export const createExtensionToken = (label) =>
  call('Create extension token', api.post('/extension-tokens', { label }));

export const revokeExtensionToken = (tokenId) =>
  call('Revoke extension token', api.delete(`/extension-tokens/${encodeURIComponent(tokenId)}`));
